import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withPropsOnChange } from 'recompose';
import { Menu, MenuItem, Popover, Chip, Avatar, Paper, IconButton } from 'material-ui';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import EyeIcon from 'material-ui/svg-icons/image/remove-red-eye';
import ShareIcon from 'material-ui/svg-icons/social/share';
import LikeIcon from 'material-ui/svg-icons/action/thumb-up';
import CommentIcon from 'material-ui/svg-icons/communication/comment';
import _ from 'lodash';
import { getEntities } from 'dash-front/selectors';
import moment from 'moment';
import { FacebookIcon, TwitterIcon } from './socialIcons';
import { addToQuery } from 'dash-front/helpers';

const openLink = url => () => {
    window.open(url);
};

const getName = publication => _.get(publication, ['meta', 'smlink', 'name']);

const PostLink = connect((state, { publicationId }) => ({
    publication: _.get(getEntities(state).publications, publicationId)
}))(({ publication }) => {
    if (!publication) {
        return null;
    }

    switch (publication.source) {
        case 'tw': {
            const account = _.get(publication, ['meta', 'account']);
            const idStr = _.get(publication, ['meta', 'id_str']);
            const url = `http://twitter.com/${account}/status/${idStr}`;
            const valid = account && idStr;
            return (
                <IconButton
                    onTouchTap={valid ? openLink(url) : undefined}
                    tooltip={valid ? getName(publication) : 'Link is broken'}
                >
                    <TwitterIcon />
                </IconButton>
            );
        }
        case 'fb': {
            const smlinkId = _.get(publication, ['meta', 'smlink', 'id']);
            const resId = _.get(publication, ['meta', 'id']);
            const url = `http://facebook.com/${smlinkId}/posts/${resId}`;
            const valid = smlinkId && resId;
            return (
                <IconButton
                    onTouchTap={valid ? openLink(url) : undefined}
                    tooltip={valid ? getName(publication) : 'Link is broken'}
                >
                    <FacebookIcon />
                </IconButton>
            );
        }
        default:
            return null;
    }
});

class PostActions extends React.PureComponent {
    state = {
        open: false,
        anchorEl: null
    };

    handleTouchTap = event => {
        event.preventDefault();
        this.setState({
            open: true,
            anchorEl: event.currentTarget
        });
    };

    handleRequestClose = () => {
        this.setState({ open: false });
    };

    onDelete = () => {
        addToQuery('delete', this.props.postId);
        this.handleRequestClose();
    };

    openLogs = () => {
        addToQuery('logs', this.props.postId);
        this.handleRequestClose();
    };

    render() {
        return (
            <div>
                <IconButton onTouchTap={this.handleTouchTap}>
                    <MoreVertIcon />
                </IconButton>
                <Popover
                    open={this.state.open}
                    anchorEl={this.state.anchorEl}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                    targetOrigin={{ horizontal: 'left', vertical: 'top' }}
                    onRequestClose={this.handleRequestClose}
                >
                    <Menu>
                        <MenuItem primaryText="Logs" onTouchTap={this.openLogs} />
                        <MenuItem style={{ color: '#ff4081' }} primaryText="Delete" onTouchTap={this.onDelete} />
                    </Menu>
                </Popover>
            </div>
        );
    }
}

PostActions.propTypes = {
    postId: PropTypes.string.isRequired
};

const initialStats = {
    likes: null,
    shares: null,
    comments: null,
    views: null
};
const generateStats = (ids, stats) => {
    if (!Array.isArray(ids) || !ids.length || !stats) {
        return { ...initialStats };
    }
    const statsAr = ids.reduce((acc, id) => (stats[id] ? acc.concat(stats[id]) : acc), []);
    if (!statsAr.length) {
        return { ...initialStats };
    }
    return statsAr.reduce(
        (acc, stat) => {
            const isFB = Boolean(stat.common);
            if (isFB) {
                const likes = _.get(stat, ['common', 'likes'], 0);
                const comments = _.get(stat, ['common', 'comments'], 0);
                const impressions = _.get(stat, ['insights', 'impressions'], 0);
                acc.likes += likes;
                acc.comments += comments;
                acc.views += impressions;
            } else {
                // twitter
                const retweets = _.get(stat, 'retweets', 0);
                const likes = _.get(stat, 'favorites', 0);
                acc.likes += likes;
                acc.shares += retweets;
            }
            return acc;
        },
        { ...initialStats }
    );
};

const getText = val => (val === null ? 'N/A' : val);
const statItems = [
    { icon: <EyeIcon />, key: 'views' },
    { icon: <ShareIcon />, key: 'shares' },
    { icon: <CommentIcon />, key: 'comments' },
    { icon: <LikeIcon />, key: 'likes' }
];
const PostStats = compose(
    connect(state => ({
        postStats: getEntities(state).postStats
    })),
    withPropsOnChange(
        (cur, next) => cur.publicationIds !== next.publicationIds || cur.postStats !== next.postStats,
        ({ publicationIds, postStats }) => generateStats(publicationIds, postStats)
    )
)(props => {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {statItems.map(el => (
                <Chip key={el.key} style={{ marginRight: 12 }}>
                    <Avatar icon={el.icon} />
                    {getText(props[el.key])}
                </Chip>
            ))}
        </div>
    );
});

const PublishedPost = connect((state, { id }) => ({
    post: id ? _.get(getEntities(state).posts, id) : null
}))(
    class _PublishedPost extends React.PureComponent {
        renderPost() {
            const { post, id } = this.props;
            const title = post.content.message || post.presentation.name;
            const { publications } = post;
            const tooltip = post.content.message ? `Post message: ${title}` : `Widget: ${post.presentation.name}`;
            return (
                <div style={{ padding: 12 }}>
                    <div style={{ position: 'relative' }}>
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                right: 0
                            }}
                        >
                            <PostActions postId={id} />
                        </div>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            marginRight: 36
                        }}
                    >
                        <p
                            style={{
                                maxWidth: 600,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                            title={tooltip}
                        >
                            {title}
                        </p>
                        <div>
                            {publications.map(publicationId => (
                                <PostLink key={publicationId} publicationId={publicationId} />
                            ))}
                        </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>{moment(post.updatedAt).format('DD MMM HH:mm')}</div>
                    <PostStats publicationIds={publications} />
                </div>
            );
        }

        render() {
            const { post } = this.props;
            return post ? <Paper>{this.renderPost()}</Paper> : null;
        }
    }
);

export default PublishedPost;
export { PostLink };



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/containers/Posts/PublishedPost.js
 **/